import styled from "styled-components";
import PageHeader from "../../features/shared/PageHeader";

const ListItem = styled.li`
  display: list-item;
  list-style-type: disc;
  margin-left: 24px;
  &::marker {
    color: #1b6dc1;
  }
`;

const Callout = styled.div`
  background: #f6f8fb;
  border-left: 4px solid #1b6dc1;
  border-radius: 4px;
  padding: 20px 24px;
`;

/**
 * Account deletion instructions.
 *
 * Google Play requires a publicly reachable page for the "Delete account URL"
 * in the Data safety form, and it has to do three specific things: name the
 * app, spell out the steps to request deletion, and state which data is
 * deleted versus which is kept and for how long. A page that only says
 * "contact us" gets the submission rejected.
 *
 * Everything below describes what the API actually does on
 * `DELETE /auth/me` — if that behaviour changes, this page has to change with
 * it.
 */
export default function DeleteAccount() {
  const sections = [
    { id: "apps", title: "Which app you are using" },
    { id: "in-app", title: "Delete from the app" },
    { id: "no-access", title: "If you cannot sign in" },
    { id: "deleted", title: "What is deleted" },
    { id: "kept", title: "What is kept, and why" },
    { id: "before", title: "Before you can delete" },
    { id: "timing", title: "How long it takes" },
    { id: "contact", title: "Contact us" },
  ];

  return (
    <div className="container page-navbar-space">
      <PageHeader>Delete Your Account</PageHeader>
      <div className="row mt-5">
        {/* LEFT SIDE: MAIN CONTENT */}
        <div
          className="col-lg-8 col-xl-9"
          style={{ color: "#444", lineHeight: "1.7" }}
        >
          <p className="mb-4 size-16 fw-normal">
            <strong>Last updated: September 6, 2026</strong>
          </p>

          <p className="mb-4 size-16 fw-normal">
            This page explains how to delete your OwnFood account and what
            happens to your data when you do. It applies to the{" "}
            <strong>OwnFood</strong> customer app, the{" "}
            <strong>Ownfood Partner</strong> seller app, and the OwnFood website
            at{" "}
            <a href="https://www.ownfood.com" className="text-decoration-none">
              https://www.ownfood.com
            </a>
            .
          </p>

          <p className="mb-5 border-bottom pb-4 size-16 fw-normal">
            Deleting your account is permanent. It cannot be undone, and we
            cannot restore an account once it has been deleted.
          </p>

          <section
            id="apps"
            style={{ scrollMarginTop: "9rem" }}
            className="mb-5"
          >
            <h4 className="fw-bold">1. Which app you are using</h4>
            <p className="size-16 fw-normal">
              You have one OwnFood account, and deleting it removes your access
              to every OwnFood app and to the website. If you both order food
              and sell food, deleting your account ends both.
            </p>
          </section>

          <section
            id="in-app"
            style={{ scrollMarginTop: "9rem" }}
            className="mb-5"
          >
            <h4 className="fw-bold">2. Delete from the app</h4>
            <p className="size-16 fw-normal">
              You can delete your account yourself, in the app, at any time.
            </p>
            <Callout className="mb-4">
              <p className="mb-2 size-16 fw-normal">
                <strong>OwnFood (customer app)</strong>
              </p>
              <p className="mb-0 size-16 fw-normal">
                Open <strong>Profile</strong>, scroll to{" "}
                <strong>Delete Account</strong>, read what will be removed,
                enter your password, and confirm.
              </p>
            </Callout>
            <Callout>
              <p className="mb-2 size-16 fw-normal">
                <strong>Ownfood Partner (seller app)</strong>
              </p>
              <p className="mb-0 size-16 fw-normal">
                Open <strong>More</strong>, tap{" "}
                <strong>Delete account</strong>, read what will be removed,
                enter your password, and confirm.
              </p>
            </Callout>
            <p className="size-16 fw-normal mt-4">
              We ask for your password so that someone who picks up an unlocked
              phone cannot delete your account.
            </p>
          </section>

          <section
            id="no-access"
            style={{ scrollMarginTop: "9rem" }}
            className="mb-5"
          >
            <h4 className="fw-bold">3. If you cannot sign in</h4>
            <p className="size-16 fw-normal">
              If you have forgotten your password, use{" "}
              <strong>Forgot password</strong> on the sign-in screen to reset it
              with a code sent to your phone, then follow the steps above.
            </p>
            <p className="size-16 fw-normal">
              If you cannot access your phone number either, email us at{" "}
              <a
                href="mailto:support@ownfood.com?subject=Account%20deletion%20request"
                className="text-decoration-none"
              >
                support@ownfood.com
              </a>{" "}
              from the email address on your account, and include the phone
              number you registered with. We will verify it is you before
              deleting anything.
            </p>
          </section>

          <section
            id="deleted"
            style={{ scrollMarginTop: "9rem" }}
            className="mb-5"
          >
            <h4 className="fw-bold">4. What is deleted</h4>
            <p className="size-16 fw-normal">
              The following is removed immediately when you confirm:
            </p>
            <ul className="ps-2 size-16 fw-normal">
              <ListItem>
                Your name, phone number, email address, date of birth and
                profile photo
              </ListItem>
              <ListItem>
                Any identity documents you uploaded, including national ID
                photos and the ID number
              </ListItem>
              <ListItem>Your saved delivery addresses</ListItem>
              <ListItem>Your saved and favourited items</ListItem>
              <ListItem>Your notifications and notification history</ListItem>
              <ListItem>
                Push notification registrations, so your devices stop receiving
                OwnFood notifications
              </ListItem>
              <ListItem>
                Any pending verification codes tied to your phone number
              </ListItem>
            </ul>
            <p className="size-16 fw-normal">
              If you sell on OwnFood, your kitchen is taken offline at the same
              time and your menu stops appearing to customers. Any active
              permission you granted a merchant to manage your kitchen is
              revoked.
            </p>
            <p className="size-16 fw-normal">
              You are signed out on every device and cannot sign in again. Your
              phone number is released, so you are free to create a new account
              with it later.
            </p>
          </section>

          <section
            id="kept"
            style={{ scrollMarginTop: "9rem" }}
            className="mb-5"
          >
            <h4 className="fw-bold">5. What is kept, and why</h4>
            <p className="size-16 fw-normal">
              We keep records of completed transactions, with your personal
              details removed from them:
            </p>
            <ul className="ps-2 size-16 fw-normal">
              <ListItem>
                Completed orders, payments, refunds, earnings and payouts. These
                are financial and tax records, and we are required to retain
                them. After deletion they no longer carry your name or contact
                details.
              </ListItem>
              <ListItem>
                Reviews you wrote stay visible so that ratings for a kitchen
                remain accurate, but they are no longer attributed to you.
              </ListItem>
            </ul>
            <p className="size-16 fw-normal">
              These anonymised records cannot be linked back to you, and we do
              not use them to contact you or to rebuild your profile.
            </p>
          </section>

          <section
            id="before"
            style={{ scrollMarginTop: "9rem" }}
            className="mb-5"
          >
            <h4 className="fw-bold">6. Before you can delete</h4>
            <p className="size-16 fw-normal">
              A few situations block deletion, because deleting would leave
              someone else stranded or leave your money with us. The app tells
              you which one applies:
            </p>
            <ul className="ps-2 size-16 fw-normal">
              <ListItem>
                You have an order in progress, either one you placed or one your
                kitchen is cooking. Wait for it to be delivered, or cancel it.
              </ListItem>
              <ListItem>
                You have a withdrawal being processed. Wait for the payout to
                complete.
              </ListItem>
              <ListItem>
                Your wallet still holds a balance. Withdraw it first — we will
                not keep money that belongs to you.
              </ListItem>
            </ul>
            <p className="size-16 fw-normal">
              Staff accounts cannot be deleted from the app and are removed by
              an administrator instead.
            </p>
          </section>

          <section
            id="timing"
            style={{ scrollMarginTop: "9rem" }}
            className="mb-5"
          >
            <h4 className="fw-bold">7. How long it takes</h4>
            <p className="size-16 fw-normal">
              Deletion through the app is immediate. Your personal data is
              removed and your session ends as soon as you confirm.
            </p>
            <p className="size-16 fw-normal">
              Requests sent by email are processed within 30 days, and usually
              much sooner. Anonymised financial records described in section 5
              are retained for as long as the law requires us to keep them.
            </p>
          </section>

          <section
            id="contact"
            style={{ scrollMarginTop: "9rem" }}
            className="mb-5"
          >
            <h4 className="fw-bold">8. Contact us</h4>
            <p className="mb-0 size-16 fw-normal">
              Email:{" "}
              <a
                href="mailto:support@ownfood.com?subject=Account%20deletion%20request"
                className="text-decoration-none"
              >
                support@ownfood.com
              </a>
            </p>
            <p className="size-16 fw-normal">
              For more on how we handle your information, see our{" "}
              <a href="/privacy-policy" className="text-decoration-none">
                Privacy Policy
              </a>
              .
            </p>
          </section>
        </div>

        {/* RIGHT SIDE: TABLE OF CONTENTS (STAY STICKY) */}
        <div className="col-lg-4 col-xl-3 d-none d-lg-block">
          <div className="sticky-top z-0" style={{ top: "9rem", zIndex: 1 }}>
            <h5 className="fw-bold mb-3 mt-1">Table of Contents</h5>
            <div className="border-start border-2">
              {sections.map((section, index) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="d-block py-2 px-3 text-decoration-none text-muted border-bottom"
                  style={{ fontSize: "1rem", transition: "0.2s" }}
                  onMouseOver={(e) => (e.target.style.color = "#007bff")}
                  onMouseOut={(e) => (e.target.style.color = "#6c757d")}
                >
                  {index + 1}. {section.title}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
